import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Field, Panel } from "@/lovable-demo/components/bits";
import {
  ClassChip,
  DispositionPanel,
  PriorityBars,
  SeverityChip,
  SeverityPanel,
  SignalStatusChip,
} from "@/lovable-demo/components/signals";
import { supabase } from "@/lovable-demo/runtime/client";
import { prioritise } from "@/lovable-demo/lib/signals/priority";
import {
  getSignal,
  listDispositions,
  listRootCause,
  listSeverity,
  listSignalLinks,
} from "@/lovable-demo/lib/signals/signals";
import { DISPOSITION_LABELS } from "@/lovable-demo/lib/signals/types";
import { fmtCurrency, fmtDate, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/signal/$id")({
  head: () => ({
    meta: [
      { title: "Signal detail — Broadview Signal Intelligence" },
      {
        name: "description",
        content:
          "Source, lineage, correlation, severity and governed disposition history for a single signal.",
      },
      { property: "og:title", content: "Signal detail — Broadview" },
      {
        property: "og:description",
        content: "Governed signal evidence with human severity and disposition.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SignalDetail,
});

function hoursSince(iso: string | null) {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

function SignalDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data, claims } = useWarranty();
  const signalQ = useQuery({
    queryKey: ["lovable-demo", "signal", id],
    queryFn: () => getSignal(id),
  });
  const linksQ = useQuery({
    queryKey: ["lovable-demo", "signal-links", id],
    queryFn: () => listSignalLinks(id),
  });
  const rcQ = useQuery({
    queryKey: ["lovable-demo", "signal-rc", id],
    queryFn: () => listRootCause([id]),
  });
  const dispQ = useQuery({
    queryKey: ["lovable-demo", "signal-disp", id],
    queryFn: () => listDispositions([id]),
  });
  const sevQ = useQuery({
    queryKey: ["lovable-demo", "signal-sev", id],
    queryFn: () => listSeverity(id),
  });
  const sourceQ = useQuery({
    queryKey: ["lovable-demo", "source-system", signalQ.data?.sourceSystemId],
    enabled: Boolean(signalQ.data?.sourceSystemId),
    queryFn: async () =>
      (
        await supabase
          .from("source_systems")
          .select("*")
          .eq("id", signalQ.data!.sourceSystemId!)
          .maybeSingle()
      ).data,
  });

  const refresh = () => void qc.invalidateQueries();
  const s = signalQ.data;

  if (signalQ.isLoading)
    return (
      <AppShell title="Loading signal…">
        <Panel>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </Panel>
      </AppShell>
    );
  if (!s)
    return (
      <AppShell title="Signal not found">
        <Panel>
          <Link to="/demo/signals" className="text-sm text-primary hover:underline">
            Back to Signal Intelligence
          </Link>
        </Panel>
      </AppShell>
    );

  const priority = prioritise(s);
  const program = data.programs.find((p) => p.id === s.programId);
  const record = data.records.find((r) => r.id === s.linkedRecordId);
  const signalClaims = claims.filter((c) => c.signalId === s.id);
  const age = hoursSince(s.sourceTimestamp);
  const freshness =
    age == null ? "unknown" : age <= 24 * 7 ? "current" : age <= 24 * 45 ? "aging" : "stale";
  const pendingSev = s.currentSeverity !== 5 && (sevQ.data ?? [])[0]?.secondApprovalRequired;
  const decided = s.status !== "open";
  const relatedCount = (linksQ.data ?? []).filter((link) => link.relatedSignalId).length;
  const verifiedCause = (rcQ.data ?? [])[0];
  const attentionFacts = [
    `${s.trendDirection} trend at ${fmtNumber(s.magnitude, s.magnitude < 10 ? 2 : 0)} ${s.magnitudeUnit}`,
    `${fmtNumber(s.exposureUnits)} units exposed`,
    `${fmtCurrency(s.financialExposure)} financial exposure`,
    `${fmtNumber(s.recurrenceCount)} recurrences`,
    `customer impact ${s.customerImpact} / 5`,
  ];
  if (s.safetyRelevant) attentionFacts.push("safety relevant");
  if (s.complianceRelevant) attentionFacts.push("compliance relevant");
  const correlationFacts = [
    relatedCount > 0
      ? `${relatedCount} related or duplicate signal${relatedCount === 1 ? "" : "s"}`
      : null,
    signalClaims.length > 0
      ? `${signalClaims.length} linked warranty claim${signalClaims.length === 1 ? "" : "s"}`
      : null,
    verifiedCause ? `verified incoming cause: ${verifiedCause.statement}` : null,
  ].filter((fact): fact is string => Boolean(fact));

  return (
    <AppShell
      title={`${s.code} — ${s.title}`}
      subtitle={`${program?.name ?? "No program"} · ${s.component}${s.supplierName ? ` · ${s.supplierName}` : ""}`}
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-4">
          <Panel>
            <div className="flex flex-wrap items-center gap-2">
              <ClassChip cls={s.signalClass} />
              <SeverityChip severity={s.currentSeverity} />
              <SignalStatusChip status={s.status} />
              {s.safetyRelevant && (
                <span className="rounded border border-risk-critical/40 px-2 py-0.5 text-[0.66rem] uppercase tracking-wider text-risk-critical">
                  Safety relevant
                </span>
              )}
              {s.complianceRelevant && (
                <span className="rounded border border-risk-high/40 px-2 py-0.5 text-[0.66rem] uppercase tracking-wider text-risk-high">
                  Compliance relevant
                </span>
              )}
            </div>
            <p className="mt-2 text-[0.7rem] leading-relaxed text-muted-foreground">
              Signal severity uses a 1–5 scale (1 Minor · 2 Low · 3 Moderate · 4 High · 5 Critical)
              for classifying incoming signals. It is separate from the 1–10 PFMEA severity scale,
              which the team assigns later during PFMEA work if this signal becomes a Problem Record
              — the two are not converted automatically.
            </p>
            <div className="mt-4 border-l-2 border-primary pl-3">
              <h2 className="text-sm font-semibold text-foreground">
                Evidence Basis — Why this signal requires attention
              </h2>
              <dl className="mt-2 space-y-1.5 text-xs leading-relaxed">
                <div>
                  <dt className="inline font-semibold text-foreground">Observed condition: </dt>
                  <dd className="inline text-muted-foreground">{s.summary}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold text-foreground">Source and readiness: </dt>
                  <dd className="inline text-muted-foreground">
                    {sourceQ.data?.name ?? s.sourceSystemId ?? "Source not identified"}
                    {s.sourceReference ? ` · ${s.sourceReference}` : ""} · {freshness} · data
                    confidence {fmtNumber(s.dataConfidence)} / 100
                  </dd>
                </div>
                <div>
                  <dt className="inline font-semibold text-foreground">Attention indicators: </dt>
                  <dd className="inline text-muted-foreground">{attentionFacts.join(" · ")}</dd>
                </div>
                {correlationFacts.length > 0 && (
                  <div>
                    <dt className="inline font-semibold text-foreground">Supporting context: </dt>
                    <dd className="inline text-muted-foreground">{correlationFacts.join(" · ")}</dd>
                  </div>
                )}
                {s.currentSeverity && s.severityRationale && (
                  <div>
                    <dt className="inline font-semibold text-foreground">Severity basis: </dt>
                    <dd className="inline text-muted-foreground">
                      S{s.currentSeverity} · {s.severityRationale}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Field
                label="Magnitude"
                value={`${fmtNumber(s.magnitude, s.magnitude < 10 ? 2 : 0)} ${s.magnitudeUnit}`}
              />
              <Field label="Trend" value={<span className="capitalize">{s.trendDirection}</span>} />
              <Field label="Exposure population" value={`${fmtNumber(s.exposureUnits)} units`} />
              <Field label="Financial exposure" value={fmtCurrency(s.financialExposure)} />
              <Field label="Recurrence" value={fmtNumber(s.recurrenceCount)} />
              <Field label="Customer impact" value={`${s.customerImpact} / 5`} />
              <Field label="Plant" value={s.plant || "—"} />
              <Field
                label="Linked record"
                value={
                  record ? (
                    <Link
                      to="/demo/problem-record/$id"
                      params={{ id: record.id }}
                      className="text-primary hover:underline"
                    >
                      {record.code}
                    </Link>
                  ) : (
                    "None"
                  )
                }
              />
            </div>
            {s.severityRationale && (
              <p className="mt-4 rounded border border-border bg-elevated/50 p-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Severity rationale: </span>
                {s.severityRationale}
              </p>
            )}
          </Panel>

          <Panel
            title="Source, lineage & data quality"
            subtitle="Provenance recorded through the Package #1 lineage service"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Source system" value={sourceQ.data?.name ?? s.sourceSystemId ?? "—"} />
              <Field
                label="Source reference"
                value={<span className="num">{s.sourceReference || "—"}</span>}
              />
              <Field
                label="Source timestamp"
                value={s.sourceTimestamp ? fmtDate(s.sourceTimestamp) : "—"}
              />
              <Field label="Ingested" value={fmtDate(s.createdAt)} />
              <Field label="Transformation" value="Normalised to Broadview canonical model v1" />
              <Field label="Freshness" value={<span className="capitalize">{freshness}</span>} />
              <Field
                label="Data confidence / readiness"
                value={`${fmtNumber(s.dataConfidence)} / 100`}
              />
              <Field label="Trust level" value={sourceQ.data?.trust_level ?? "—"} />
            </div>
          </Panel>

          <Panel
            title="Correlation"
            subtitle="Duplicate and related signals, and the claims behind this signal"
          >
            {(linksQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No related or duplicate signals linked.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {(linksQ.data ?? []).map((l) => {
                  const other = l.signalId === s.id ? l.relatedSignalId : l.signalId;
                  return (
                    <li key={l.id} className="rounded border border-border p-2">
                      <span className="mr-2 rounded bg-elevated px-1.5 py-0.5 text-[0.66rem] uppercase tracking-wider text-muted-foreground">
                        {l.linkType}
                      </span>
                      {other && (
                        <Link
                          to="/demo/signal/$id"
                          params={{ id: other }}
                          className="num text-primary hover:underline"
                        >
                          {other.toUpperCase()}
                        </Link>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">{l.rationale}</p>
                    </li>
                  );
                })}
              </ul>
            )}
            {signalClaims.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                {signalClaims.length} warranty claims roll up to this signal (
                {fmtCurrency(signalClaims.reduce((a, c) => a + c.totalCost, 0))} claimed).
              </p>
            )}
          </Panel>

          <Panel
            title="Incoming verified root cause"
            subtitle="Preserved as evidence — no re-analysis forced solely because the issue entered Broadview"
          >
            {(rcQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No verified root cause arrived with this signal.
              </p>
            ) : (
              (rcQ.data ?? []).map((rc) => (
                <div key={rc.id} className="rounded border border-border p-3 text-sm">
                  <p className="text-foreground">{rc.statement}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {rc.source} · verified by {rc.verifiedBy} · {rc.verificationMethod} · ref{" "}
                    <span className="num">{rc.evidenceRef}</span>
                    {rc.carriedToRecordId && (
                      <>
                        {" "}
                        · carried to{" "}
                        <span className="num">{rc.carriedToRecordId.toUpperCase()}</span>
                      </>
                    )}
                  </p>
                </div>
              ))
            )}
          </Panel>

          <Panel
            title="Disposition & severity history"
            subtitle="Append-only; every governed decision is reconstructable"
          >
            <ol className="space-y-2">
              {(dispQ.data ?? []).map((d) => (
                <li key={d.id} className="rounded border border-border p-2 text-sm">
                  <p className="font-semibold text-foreground">
                    {DISPOSITION_LABELS[d.disposition]}
                    {d.targetRecordId && (
                      <span className="num"> → {d.targetRecordId.toUpperCase()}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">{d.rationale}</p>
                  <p className="mt-1 text-[0.7rem] text-muted-foreground">
                    {fmtDate(d.createdAt)} · {d.decidedByLabel || "—"} · authority{" "}
                    {d.authorityRuleKey ?? "—"}
                    {d.auditEventId ? " · audit confirmed · evidence snapshot captured" : ""}
                  </p>
                </li>
              ))}
              {(sevQ.data ?? []).map((a) => (
                <li key={a.id} className="rounded border border-border p-2 text-sm">
                  <p className="font-semibold text-foreground">
                    Severity {a.severity} assessed
                    {a.secondApprovalRequired ? " — awaiting second approval" : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">{a.rationale}</p>
                  <p className="mt-1 text-[0.7rem] text-muted-foreground">
                    {fmtDate(a.createdAt)} · {a.assessedByLabel} · AI suggestion{" "}
                    {a.aiSuggested ?? "—"} (advisory)
                  </p>
                </li>
              ))}
              {(dispQ.data ?? []).length === 0 && (sevQ.data ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">No governed decisions yet.</p>
              )}
            </ol>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title={`Signal priority — ${priority.score}`}>
            <PriorityBars breakdown={priority} />
          </Panel>
          <Panel
            title="Severity (human authority)"
            subtitle={pendingSev ? "Severity 5 awaiting independent second approval" : undefined}
          >
            <SeverityPanel key={s.id} signal={s} onDone={refresh} />
          </Panel>
          <Panel
            title="Governed disposition"
            subtitle={
              decided
                ? `Current: ${s.currentDisposition ? DISPOSITION_LABELS[s.currentDisposition] : s.status}. A new decision is added to history.`
                : "Awaiting a human decision"
            }
          >
            {s.status === "converted" ? (
              <p className="text-sm text-muted-foreground">
                A Problem Record was created from this signal. Continue in{" "}
                {record && (
                  <Link
                    to="/demo/problem-record/$id"
                    params={{ id: record.id }}
                    className="text-primary hover:underline"
                  >
                    {record.code}
                  </Link>
                )}
                .
              </p>
            ) : (
              <DispositionPanel key={s.id} signal={s} onDone={refresh} />
            )}
          </Panel>
        </div>
      </div>
    </AppShell>
  );
}
